// 法人ユーザー情報編集
import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import { Button } from 'react-bootstrap';
import axios from '../../../axios';
import { api_response_type } from '../../../Const';
import { handleBrowserBack } from '../../../BrowserBack';
import { isBrowserIe } from '../../../BrowserJudgment';
import {
  CorporateDataListType,
  ResponseDetailType,
  CorporateRequestType,
  ValidMessageType,
} from '../../../types/Corporate';
import { CorporateUserDataList, UsersLabel } from '../../../const/Corporate';
import LoadingModal from '../../../molecules/LoadingModal';
import CorporateCreateDefinitionDescription from '../../../organisms/CorporateCreateDefinitionDescription';
import CorporateInfoConfirm from '../../../organisms/CorporateInfoConfirm';
import CorporateCreateEditComment from '../../../organisms/CorporateCreateEditComment';
import { KWMNTGM05030A01, KWMNTGM05050A02, KWMNTGM05060A02 } from '../../../const/api_modules';

const CorporateUserInfoEdit = () => {
  const [data, setData] = useState<ResponseDetailType | undefined>(undefined);
  const [verified_data, setVerifiedData] = useState<ResponseDetailType | undefined>(undefined);
  const [edit_comment, setEditComment] = useState('');
  const [is_loading, setIsLoading] = useState(false);
  // 確認画面を表示するフラグ
  const [is_confirm, setIsConfirm] = useState(false);
  // エラーメッセージ
  const [valid_message, setValidMessage] = useState<ValidMessageType[]>([]);

  // URLパラメータの取得
  const url_params = new URLSearchParams(document.location.search.substring(1));
  const corporate_id = url_params.get('corporate_id');
  const user_type = url_params.get('user_type');
  // 'user1' or 'user2' or 'user3' から数字だけ取り出しておく
  const user_type_id = user_type?.replace(/[^0-9]/g, '');

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    // 初期値のデータを取得
    getData();
  }, []);

  // 初期値のデータを取得
  const getData = async () => {
    setIsLoading(true);
    try {
      const detail = [{ id: 0, name: 'id', value: corporate_id }];

      const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/common`, {
        module_name : KWMNTGM05030A01,
        is_json : true,
        reception_id : '0',
        reception_cd : null,
        detail : detail,
      });

      if (response.data.result) {
        // 取得したデータをstateにセット
        setData(response.data.detail);
        setEditComment(response.data.detail.corporate_information.temp_comment);
      }
    } catch (error) {
      console.error(error.response.data.message);
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * インプットフィールドに入力された値をstateにセットする処理
   * @param event
   */
  const handleInput = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    const item_name = name + '_' + user_type_id;

    data &&
      setData({
        ...data,
        corporate_information: { ...data.corporate_information, [item_name]: value },
      });
  };

  /**
   * 登録ボタン押下時の処理
   */
  const handleRegister = async () => {
    try {
      // ローディング画面表示
      setIsLoading(true);
      // バリデーション初期化
      setValidMessage([]);

      let detail: CorporateRequestType[] = [];
      const info = data?.corporate_information;
      const email = `temp_email_${user_type_id}`;
      const position = `temp_position_name_${user_type_id}`;
      const name = `temp_name_${user_type_id}`;
      if (info) {
        detail = [
          { id: 0, name: 'id', value: info.id },
          { id: 0, name: email, value: info[email] },
          { id: 0, name: position, value: info[position] },
          { id: 0, name: name, value: info[name] },
          { id: 0, name: 'temp_comment', value: edit_comment },
        ];
      }

      const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/common`, {
        module_name : KWMNTGM05060A02,
        is_json : true,
        reception_id : '0',
        reception_cd : null,
        detail : detail,
      });

      // APIからの応答種別を判定
      if (response.data.response_type === api_response_type.validation) {
        // バリデーションエラーをセット
        setValidMessage(response.data.detail);
      } else if (
        response.data.response_type === api_response_type.transition_to_corporate_entry_confirm
      ) {
        // チェック済のデータをセットして確認画面を表示
        setVerifiedData(response.data.detail);
        setIsConfirm(true);
      }
    } catch (error) {
      console.error(error.response.data.message);
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 【確認】ボタン押下時の処理
   */
  const handleConfirm = async () => {
    try {
      // ローディング画面表示
      setIsLoading(true);

      let detail: CorporateRequestType[] = [];
      const info = verified_data?.corporate_information;
      const email = `temp_email_${user_type_id}`;
      const position = `temp_position_name_${user_type_id}`;
      const name = `temp_name_${user_type_id}`;
      if (info) {
        detail = [
          { id: 0, name: 'id', value: info.id },
          { id: 0, name: email, value: info[email] },
          { id: 0, name: position, value: info[position] },
          { id: 0, name: name, value: info[name] },
          { id: 0, name: 'temp_comment', value: info.temp_comment },
        ];
      }

      const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/common`, {
        module_name : KWMNTGM05050A02,
        is_json : true,
        reception_id : '0',
        reception_cd : null,
        detail : detail,
      });

      // APIからの応答種別を判定
      if (response.data.response_type === api_response_type.navigation_finish) {
        // 完了画面に遷移
        location.replace(
          `${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_info_edit_complete`
        );
      }
    } catch (error) {
      console.error(error.response.data.message);
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 【キャンセル】ボタン押下時の処理
   */
  const backTransition = () => {
    if (isBrowserIe()) {
      const node = document.getElementById('corporate-user-info-edit');
      location.replace(node?.dataset.referer as string);
    } else {
      location.replace(document.referrer);
    }
  };

  return (
    <div className="corporate">
      <div className="title">法人情報</div>
      <div className="container">
        <div className="contents">
          {!is_confirm ? (
            <>
              <p className="contents_title mb-5">
                {UsersLabel.map(item => item.type === user_type && [item.label] + '編集')}
              </p>
              <div className="input_field_wrapper">
                {CorporateUserDataList.map((item: CorporateDataListType) => (
                  <dl
                    className={`field_section ${item.name === 'temp_comment' ? 'mt-5' : ''}`}
                    key={item.name}
                  >
                    <dt className="field_label">{item.label}</dt>
                    <CorporateCreateDefinitionDescription
                      item={item}
                      data={data}
                      handleInput={handleInput}
                      valid_message={valid_message}
                      user_type_id={user_type_id}
                    />
                  </dl>
                ))}
                <CorporateCreateEditComment
                  margin="mt-4"
                  edit_comment={edit_comment}
                  setEditComment={setEditComment}
                  valid_message={valid_message}
                />
              </div>
              <div className="w_button_section mx-auto my-5 d-flex justify-content-between">
                <Button
                  className="btn-mini btn-text-color"
                  variant="outline-success"
                  onClick={backTransition}
                >
                  キャンセル
                </Button>
                <Button className="btn-mini btn-success" onClick={handleRegister}>
                  登録
                </Button>
              </div>
            </>
          ) : (
            <CorporateInfoConfirm
              verified_data={verified_data}
              setIsConfirm={setIsConfirm}
              handleConfirm={handleConfirm}
            />
          )}
        </div>
      </div>
      <LoadingModal show={is_loading} />
    </div>
  );
};

if (document.getElementById('corporate-user-info-edit')) {
  render(<CorporateUserInfoEdit />, document.getElementById('corporate-user-info-edit'));
}
